import { ThunkDispatch } from '@reduxjs/toolkit';
import { UnknownAction } from 'redux';
import { refreshAccessToken, clearUserState } from '../actions/user.actions';
import RootState from '../states/root.state';

let refreshTimeout: NodeJS.Timeout | null = null;
let visibilityHandler: (() => void) | null = null;

export const setupTokenRefresh = (
  dispatch: ThunkDispatch<RootState, unknown, UnknownAction>,
  expirationTime: number
) => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }

  const currentTime = Date.now();
  const timeUntilExpiry = expirationTime - currentTime;
  const refreshTime = timeUntilExpiry > 0 ? timeUntilExpiry - 60000 : 0; // Refresh 1 minute before expiry

  if (refreshTime > 0) {
    console.log('[TokenManager] Setting up token refresh in', Math.round(refreshTime / 1000), 'seconds (', new Date(currentTime + refreshTime).toISOString(), ')');
    refreshTimeout = setTimeout(() => {
      console.log('[TokenManager] Token refresh timeout fired, refreshing token...');
      refreshTimeout = null;
      dispatch(refreshAccessToken());
    }, refreshTime);
  } else if (timeUntilExpiry > 0) {
    // Token expiring within the buffer window — refresh immediately instead of logging out
    console.log('[TokenManager] Token expiring soon, refreshing immediately');
    dispatch(refreshAccessToken());
  } else {
    // Token is fully expired — try to refresh via refresh cookie, only clear if that fails
    console.log('[TokenManager] Token expired, attempting refresh');
    dispatch(refreshAccessToken());
  }

  // Set up visibility change listener to catch tab wake-ups
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    window.removeEventListener('online', visibilityHandler);
  }
  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      const storedExpiration = parseInt(localStorage.getItem('expirationTime') ?? '0', 10);
      if (storedExpiration && storedExpiration - now < 60000) {
        console.log('[TokenManager] Tab visible or back online, token expired or expiring soon — refreshing');
        dispatch(refreshAccessToken());
      }
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);
  // After waking, the refresh can fail before the network is back; retry once it is.
  window.addEventListener('online', visibilityHandler);
};

export const clearTokenRefresh = () => {
  if (refreshTimeout) {
    console.log('[TokenManager] Clearing token refresh timeout');
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    window.removeEventListener('online', visibilityHandler);
    visibilityHandler = null;
  }
};